/**
 * useFeedTimeline — orquestra dados + WebSocket para o feed (Story 2.4.3).
 *
 * Responsabilidades:
 * - Lotes de 6 com "Carregar mais" (``GET /feed/timeline``) via ``useInfiniteQuery``:
 *   o 1º traz os destaques (fixados + anúncios ativos) fora da conta; os seguintes
 *   vêm do cursor ``proximo`` que o servidor devolve.
 * - Mutations (create, edit, pin, delete, react) com invalidação otimista.
 * - Subscribe no WebSocket ``/api/v1/ws/feed?token=...`` e aplicação dos
 *   eventos no cache do React Query — sem refetch global, sem flicker.
 *
 * Reuso: estrutura de reconnect/backoff é adaptada do
 * ``useEquipmentWebSocket``; URL helper vem de ``@/config/api`` (mesmo
 * padrão de ``NotificationContext``).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
} from "@tanstack/react-query";

import { getWsUrl } from "@/config/api";
import { useAuth } from "@/contexts/AuthContext";
import feedService, { type FeedPostCreateInput } from "@/services/feedService";
import {
  FeedCursor,
  FeedPinContext,
  FeedPinScope,
  FeedPost,
  FeedReactors,
  FeedWsEvent,
} from "@/types/feed";
import { enqueue } from "@/lib/offline-queue";
import { isNetworkError } from "@/hooks/useOfflineQueue";

/** Também usado por ``useNovidadesDoFeed`` (o socket do menu): um só endereço para os dois. */
export const FEED_WS_PATH = "/api/v1/ws/feed";
/** Posts comuns por lote do "Carregar mais" (pedido de 26/10/2026); os destaques não contam. */
export const POSTS_POR_LOTE = 6;
const RECONNECT_INTERVAL_MS = 3000;
const MAX_RECONNECT_ATTEMPTS = 5;

/** Um lote no cache: os destaques já vêm na frente dos comuns (só no 1º lote). */
interface Lote {
  posts: FeedPost[];
  proximo: FeedCursor | null;
}

type Pages = InfiniteData<Lote, FeedCursor | undefined>;

const FEED_QUERY_KEY = ["feed", "timeline"] as const;
const FEED_PIN_CONTEXT_KEY = ["feed", "pin-context"] as const;

/**
 * "Este post está fixado PRA ESTE viewer?" — versão cliente da ordenação
 * por-viewer (E17.2.2). Global flutua pra todos; setor só pros membros do
 * setor alvo. Usada pra recomputar ``pinned`` nos eventos WS (o evento traz
 * ``pin_scope``/``pin_scope_target_id`` crus — o mesmo payload vai a todos que
 * veem o post, então cada cliente decide com o próprio setor).
 */
export const effectivePinned = (post: FeedPost, viewerSectorId: string | null): boolean => {
  if (post.pin_scope === "global") return true;
  if (post.pin_scope === "sector") return post.pin_scope_target_id === viewerSectorId;
  return false;
};

/** E17.3.5 — anúncio ativo (espelha o tier do servidor: now < expires_at). */
const isActiveAnnouncement = (p: FeedPost): boolean =>
  p.post_type === "announcement" &&
  !!p.announcement_expires_at &&
  new Date(p.announcement_expires_at).getTime() > Date.now();

/**
 * Reordena uma lista de posts espelhando o tier do servidor (E17.2.2/E17.3.5):
 * pinados primeiro, depois anúncios ATIVOS, depois o resto — cada bucket por
 * ``created_at`` DESC. Usado quando um evento WS (pin/post novo) chega, pra
 * manter a ordem servidor-side sem refetch (senão o anúncio afundaria).
 */
const reorderPinnedFirst = (posts: FeedPost[]): FeedPost[] => {
  const pinned: FeedPost[] = [];
  const announcements: FeedPost[] = [];
  const others: FeedPost[] = [];
  for (const p of posts) {
    if (p.pinned) pinned.push(p);
    else if (isActiveAnnouncement(p)) announcements.push(p);
    else others.push(p);
  }
  const byCreatedDesc = (a: FeedPost, b: FeedPost) => b.created_at.localeCompare(a.created_at);
  pinned.sort(byCreatedDesc);
  announcements.sort(byCreatedDesc);
  others.sort(byCreatedDesc);
  return [...pinned, ...announcements, ...others];
};

/**
 * Atualiza um post pelo id em todas as páginas. Se o updater retornar
 * ``null``, o post é removido (não usamos hoje — feed mantém deleted
 * com ``deleted_at`` set).
 */
const updatePostInPages = (
  pages: FeedPost[][],
  postId: string,
  updater: (p: FeedPost) => FeedPost
): FeedPost[][] => {
  return pages.map((page) => page.map((p) => (p.id === postId ? updater(p) : p)));
};

/** Chave da lista de "quem reagiu" de um post (passo 15) — só tem consulta ATIVA com a janela aberta. */
const reactionsKey = (postId: string) => ["feed", "reactions", postId] as const;

/**
 * Soma ou desconta uma reação no post. A do PRÓPRIO viewer chega duas vezes —
 * a resposta do POST e o eco ao vivo, que o servidor manda também para quem
 * clicou (e o reenvio da fila offline ecoa do mesmo jeito) — e só a primeira
 * conta: ``viewer_reactions`` já registra se ela foi contada. A de outra
 * pessoa não tem esse registro e conta sempre; não duplica porque o hook
 * mantém uma conexão só e o servidor entrega uma vez por conexão.
 *
 * ``reactors`` (passo 15, "quem reagiu"): quando o servidor manda (resposta do
 * POST ou evento ao vivo), ele é ABSOLUTO e substitui o do post — idempotente,
 * ao contrário de um delta. Sem ele (fila offline, ou worker antigo durante o
 * deploy), só a reação do PRÓPRIO viewer mexe: o ``total`` de pessoas muda na
 * transição de "nenhum emoji meu" para "algum" (+1) e de volta (−1); a amostra
 * fica. Reação de outra pessoa sem ``reactors`` não tem como ser contada aqui —
 * o evento seguinte, já com o valor absoluto, corrige. O ``reactors`` absoluto
 * vale mesmo quando a reação do viewer já estava contada (o eco do reenvio
 * offline chega assim e corrige a estimativa).
 */
const aplicarReacao = (
  p: FeedPost,
  emoji: string,
  isAdd: boolean,
  doViewer: boolean,
  reactors?: FeedReactors
): FeedPost => {
  if (doViewer && (p.viewer_reactions ?? []).includes(emoji) === isAdd) {
    // Já contada (a resposta do POST ou o otimista da fila offline): a contagem
    // do chip não muda, mas o `reactors` absoluto do servidor ainda vale — é ele
    // que corrige a estimativa do otimista quando o eco do reenvio chega.
    return reactors ? { ...p, reactors } : p;
  }
  const summary = { ...p.reaction_summary };
  const current = summary[emoji] ?? 0;
  summary[emoji] = isAdd ? current + 1 : Math.max(0, current - 1);
  if (summary[emoji] === 0) delete summary[emoji];
  if (!doViewer) {
    return { ...p, reaction_summary: summary, ...(reactors ? { reactors } : {}) };
  }
  const antes = p.viewer_reactions ?? [];
  const set = new Set(antes);
  if (isAdd) set.add(emoji);
  else set.delete(emoji);
  const next: FeedPost = {
    ...p,
    reaction_summary: summary,
    viewer_reactions: Array.from(set),
  };
  if (reactors) {
    next.reactors = reactors;
  } else if (p.reactors && antes.length > 0 !== set.size > 0) {
    next.reactors = {
      ...p.reactors,
      total: Math.max(0, p.reactors.total + (set.size > 0 ? 1 : -1)),
    };
  }
  return next;
};

const findPost = (pages: FeedPost[][], postId: string): FeedPost | null => {
  for (const page of pages) {
    for (const p of page) {
      if (p.id === postId) return p;
    }
  }
  return null;
};

export interface UseFeedTimelineResult {
  posts: FeedPost[];
  isLoading: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  loadMore: () => void;
  /** O último "Carregar mais" falhou (os posts já carregados ficam). */
  loadMoreError: boolean;
  /** Falha do feed em si (o 1º lote ou a recarga); a do "Carregar mais" fica no ``loadMoreError``. */
  error: Error | null;
  wsConnected: boolean;
  reconnectWs: () => void;
  /** Capacidades de pin do viewer (E17.2.2); null enquanto carrega. */
  pinContext: FeedPinContext | null;
  composer: {
    create: (input: FeedPostCreateInput) => Promise<FeedPost>;
    isCreating: boolean;
  };
  actions: {
    react: (postId: string, emoji: string) => Promise<void>;
    pin: (postId: string, scope: FeedPinScope, targetSectorId?: string | null) => Promise<void>;
    remove: (postId: string) => Promise<void>;
    edit: (postId: string, content: string) => Promise<FeedPost>;
    votePoll: (postId: string, optionId: string) => Promise<void>;
  };
}

export function useFeedTimeline(): UseFeedTimelineResult {
  const queryClient = useQueryClient();
  const { user, getToken } = useAuth();
  const currentUserId = user?.user_id ?? null;

  const query = useInfiniteQuery<Lote, Error, Pages, typeof FEED_QUERY_KEY, FeedCursor | undefined>(
    {
      queryKey: FEED_QUERY_KEY,
      queryFn: async ({ pageParam }) => {
        const lote = await feedService.listarLoteDaTimeline({
          limit: POSTS_POR_LOTE,
          ...pageParam,
        });
        return { posts: [...lote.destaques, ...lote.posts], proximo: lote.proximo };
      },
      initialPageParam: undefined,
      getNextPageParam: (lastPage) => lastPage.proximo ?? undefined,
    }
  );

  const posts = useMemo<FeedPost[]>(() => {
    if (!query.data) return [];
    // Dedup por id, mantendo a 1ª ocorrência: o servidor não repete destaque nos
    // lotes seguintes, mas um anúncio que expira ou um post desafixado depois do
    // 1º lote volta a ser comum e pode chegar de novo num lote seguinte.
    const seen = new Set<string>();
    const out: FeedPost[] = [];
    for (const p of query.data.pages.flatMap((lote) => lote.posts)) {
      if (!seen.has(p.id)) {
        seen.add(p.id);
        out.push(p);
      }
    }
    return out;
  }, [query.data]);

  // Capacidades de pin do viewer (E17.2.2). Também entrega o ``viewer_sector_id``
  // que o token JWT não carrega — usado pra recomputar ``pinned`` nos eventos WS.
  const pinContextQuery = useQuery<FeedPinContext>({
    queryKey: FEED_PIN_CONTEXT_KEY,
    queryFn: () => feedService.getPinContext(),
    enabled: !!currentUserId,
    staleTime: 60_000,
  });
  const pinContext = pinContextQuery.data ?? null;

  // Ref pra o WS handler (callback estável) ler o setor do viewer sem reconectar.
  const viewerSectorIdRef = useRef<string | null>(null);
  useEffect(() => {
    viewerSectorIdRef.current = pinContext?.viewer_sector_id ?? null;
  }, [pinContext?.viewer_sector_id]);

  const setPagesData = useCallback(
    (mutator: (pages: FeedPost[][]) => FeedPost[][]) => {
      queryClient.setQueryData<Pages>(FEED_QUERY_KEY, (old) => {
        if (!old) return old;
        // Os eventos mexem nos posts; o cursor de cada lote (o que o servidor
        // mandou) fica: o próximo "Carregar mais" continua de onde parou.
        const novas = mutator(old.pages.map((lote) => lote.posts));
        return {
          ...old,
          pages: novas.map((posts, i) => ({ posts, proximo: old.pages[i]?.proximo ?? null })),
        };
      });
    },
    [queryClient]
  );

  /**
   * Story X1.3.3 — aplica o toggle de reaction localmente quando NÃO há resposta
   * do servidor (offline). Deriva add/remove do `viewer_reactions` atual (vs. o
   * `onSuccess`/WS, que usam o `action` do servidor). O reenvio da fila ecoa ao
   * vivo como qualquer clique; o `aplicarReacao` não conta o eco de novo.
   */
  const applyReactionOptimistic = useCallback(
    (postId: string, emoji: string) => {
      setPagesData((pages) =>
        updatePostInPages(pages, postId, (p) =>
          aplicarReacao(p, emoji, !(p.viewer_reactions ?? []).includes(emoji), true)
        )
      );
    },
    [setPagesData]
  );

  const upsertPost = useCallback(
    (post: FeedPost) => {
      setPagesData((pages) => {
        const existing = findPost(pages, post.id);
        if (existing) {
          return updatePostInPages(pages, post.id, () => post);
        }
        if (pages.length === 0) return [[post]];
        const [first, ...rest] = pages;
        return [reorderPinnedFirst([post, ...first]), ...rest];
      });
    },
    [setPagesData]
  );

  // --- Mutations ---------------------------------------------------------

  const createMutation = useMutation({
    mutationFn: (input: FeedPostCreateInput) => feedService.createPost(input),
    onSuccess: (post) => upsertPost(post),
  });

  // Cliques no mesmo post+emoji saem um de cada vez: o 2º só depois da resposta
  // do 1º. Com os dois no ar ao mesmo tempo, o servidor pode processá-los em
  // qualquer ordem e as respostas chegam fora de ordem — nenhuma regra aqui
  // acertaria todos os casos (pôr e tirar rápido deixava o contador errado).
  const filaDeCliquesRef = useRef(new Map<string, Promise<unknown>>());

  const reactMutation = useMutation({
    mutationFn: ({ postId, emoji }: { postId: string; emoji: string }) =>
      feedService.toggleReaction(postId, emoji),
    // Sem rede, o clique tenta e cai no erro de rede → fila offline (durável,
    // `actions.react`). No padrão ("online"), o clique que esperava na fila de
    // cliques ficaria pausado na memória e sumiria se a página recarregasse.
    networkMode: "always",
    onSuccess: ({ action, post_id, emoji, user_id, reactors }) => {
      const doViewer = !!currentUserId && user_id === currentUserId;
      const isAdd = action === "added";
      setPagesData((pages) =>
        updatePostInPages(pages, post_id, (p) => {
          // O eco ao vivo sai ANTES desta resposta, com o mesmo `reactors`. Se ele já foi
          // aplicado (a reação do viewer já está contada), a resposta — mais lenta — pode ser
          // mais velha que o evento de OUTRA pessoa que chegou no meio: não vale mais que ele.
          // O caminho offline não passa por aqui (o reenvio chega só pelo eco), então a
          // correção da estimativa do otimista segue intacta.
          const jaContada = doViewer && (p.viewer_reactions ?? []).includes(emoji) === isAdd;
          return aplicarReacao(p, emoji, isAdd, doViewer, jaContada ? undefined : reactors);
        })
      );
      // Lista de "quem reagiu" aberta neste post (D4-a): a linha nova entra sozinha.
      void queryClient.invalidateQueries({
        queryKey: reactionsKey(post_id),
        refetchType: "active",
      });
    },
  });

  const pinMutation = useMutation({
    mutationFn: ({
      postId,
      scope,
      targetSectorId,
    }: {
      postId: string;
      scope: FeedPinScope;
      targetSectorId?: string | null;
    }) => feedService.setPin(postId, scope, targetSectorId),
    onSuccess: (post) => {
      // A resposta já traz ``pinned`` efetivo pro ator (o backend computou com o
      // setor dele). Pin/unpin muda contagens → invalida o pin-context (limites).
      upsertPost(post);
      void queryClient.invalidateQueries({ queryKey: FEED_PIN_CONTEXT_KEY });
    },
  });

  const editMutation = useMutation({
    mutationFn: ({ postId, content }: { postId: string; content: string }) =>
      feedService.updatePost(postId, { content }),
    onSuccess: (post) => upsertPost(post),
  });

  const deleteMutation = useMutation({
    mutationFn: (postId: string) => feedService.deletePost(postId),
    onSuccess: (_, postId) => {
      setPagesData((pages) =>
        updatePostInPages(pages, postId, (p) => ({
          ...p,
          deleted_at: new Date().toISOString(),
        }))
      );
    },
  });

  // E17.2.1 — voto em enquete. Sem WS (igual comentários): o resultado volta
  // na resposta e atualizamos o ``poll`` do post no cache (revela contagens).
  const votePollMutation = useMutation({
    mutationFn: ({ postId, optionId }: { postId: string; optionId: string }) =>
      feedService.votePoll(postId, optionId),
    onSuccess: (poll, { postId }) => {
      setPagesData((pages) => updatePostInPages(pages, postId, (p) => ({ ...p, poll })));
    },
  });

  // --- WebSocket ---------------------------------------------------------

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const [wsConnected, setWsConnected] = useState(false);

  const handleWsEvent = useCallback(
    (event: FeedWsEvent) => {
      // O evento chega SÓ pra quem o servidor decidiu que vê o post (PR 3,
      // ``_publicar_evento_do_post`` no backend): canal da empresa pra post
      // público; canal pessoal (+ canal-sombra do admin) pra post de
      // setor/grupo. Não é mais um broadcast company-wide onde cada cliente
      // filtrava sozinho — mas o ``pinned`` que chega ainda é o espelho cru
      // (pin global/setor), então recomputa o efetivo PRA ESTE viewer
      // (E17.2.2 — pin de setor só flutua pro setor).
      const withViewerPin = (post: FeedPost): FeedPost => ({
        ...post,
        pinned: effectivePinned(post, viewerSectorIdRef.current),
      });
      switch (event.type) {
        case "post:new":
          upsertPost(withViewerPin(event.post));
          break;
        case "post:updated":
          // I1 (PR 3 fix-brief) — quem GANHOU acesso numa edição de escopo
          // (setor A → setor B, ou restrito → público) nunca recebeu o
          // post:new: o roteamento por visibilidade (PR 3) não é mais
          // company-wide, então "só quem já tem no cache atualiza" deixava
          // quem ganhou acesso sem o post até sair/voltar do feed ou
          // recarregar (não há refetch automático ao focar a janela). Post
          // DESCONHECIDO insere (mesmo padrão do post:pinned/unpinned
          // abaixo); post CONHECIDO só atualiza, preservando ``poll`` e
          // ``viewer_reactions`` do viewer — estado por-viewer que o
          // broadcast NÃO carrega (poll=None, viewer_reactions=[]).
          setPagesData((pages) => {
            const incoming = withViewerPin(event.post);
            const exists = findPost(pages, event.post.id);
            if (!exists) {
              if (pages.length === 0) return [[incoming]];
              const [first, ...rest] = pages;
              return [reorderPinnedFirst([incoming, ...first]), ...rest];
            }
            return updatePostInPages(pages, event.post.id, (p) => ({
              ...incoming,
              poll: p.poll,
              viewer_reactions: p.viewer_reactions,
              // "Quem reagiu" também: um worker antigo (deploy em curso) não manda
              // `reactors`, e o post não pode perder a linha por causa disso.
              reactors: incoming.reactors ?? p.reactors,
            }));
          });
          break;
        case "post:pinned":
        case "post:unpinned":
          setPagesData((pages) => {
            const incoming = withViewerPin(event.post);
            const exists = findPost(pages, event.post.id);
            if (!exists) {
              if (pages.length === 0) return [[incoming]];
              const [first, ...rest] = pages;
              return [reorderPinnedFirst([incoming, ...first]), ...rest];
            }
            // Preserva o estado POR-VIEWER (poll + viewer_reactions): o broadcast
            // de pin manda poll=None e viewer_reactions=[] (são por-viewer, não
            // viajam no fio). Sem isto, fixar um post zeraria o voto/reações do
            // viewer no cache até o próximo refetch — mesma proteção do
            // post:updated. reaction_summary vem fresco do broadcast (agregado).
            const updated = updatePostInPages(pages, event.post.id, (p) => ({
              ...incoming,
              poll: p.poll,
              viewer_reactions: p.viewer_reactions,
              reactors: incoming.reactors ?? p.reactors,
            }));
            // Reorder only the first page — older pages stay locked to
            // their cursor windows. Mostly the pinned post lives on first
            // page anyway.
            if (updated.length === 0) return updated;
            const [first, ...rest] = updated;
            return [reorderPinnedFirst(first), ...rest];
          });
          break;
        case "post:deleted":
          setPagesData((pages) =>
            updatePostInPages(pages, event.post_id, (p) => ({
              ...p,
              deleted_at: event.deleted_at ?? new Date().toISOString(),
            }))
          );
          break;
        case "reaction:added":
        case "reaction:removed": {
          const isAdd = event.type === "reaction:added";
          const doViewer = !!currentUserId && event.user_id === currentUserId;
          setPagesData((pages) =>
            updatePostInPages(pages, event.post_id, (p) =>
              aplicarReacao(p, event.emoji, isAdd, doViewer, event.reactors)
            )
          );
          // Lista de "quem reagiu" aberta neste post (D4-a): refaz só a consulta
          // ATIVA. Com a janela fechada não há consulta ativa, então não há requisição.
          void queryClient.invalidateQueries({
            queryKey: reactionsKey(event.post_id),
            refetchType: "active",
          });
          break;
        }
        case "pong":
        default:
          break;
      }
    },
    [currentUserId, queryClient, setPagesData, upsertPost]
  );

  // O AuthProvider cria um ``getToken`` novo a cada redesenho dele; lido por
  // ref, ele não muda o ``connect`` nem derruba e reabre a conexão.
  const getTokenRef = useRef(getToken);
  useEffect(() => {
    getTokenRef.current = getToken;
  }, [getToken]);

  /**
   * Larga a conexão atual. O ``wsRef`` esvazia ANTES do ``close()``: no
   * navegador o ``onclose`` chega depois, quando a conexão nova já pode estar
   * no ref, e os handlers só agem se a conexão deles ainda é a do ref.
   */
  const largarConexao = useCallback(() => {
    const ws = wsRef.current;
    wsRef.current = null;
    ws?.close();
  }, []);

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;
    largarConexao();

    const token = getTokenRef.current();
    if (!token) return;

    const url = `${getWsUrl(FEED_WS_PATH)}?token=${encodeURIComponent(token)}`;
    let ws: WebSocket;
    try {
      ws = new WebSocket(url);
    } catch (err) {
      console.error("[FeedWS] failed to construct WebSocket:", err);
      return;
    }
    wsRef.current = ws;

    // Conexão já largada (reconectar, sair do Feed) não mexe em nada: sem
    // isto, o ``onclose`` dela apagava do ref a conexão nova, que ficava
    // aberta sem dono e somava cada evento de novo (contador duplicando). No
    // ``onopen`` e no ``onmessage`` é defesa: o navegador descarta mensagem
    // fora do OPEN, e o ``open`` de uma conexão largada é uma janela mínima.
    const ehAtual = () => wsRef.current === ws;

    ws.onopen = () => {
      if (!ehAtual()) return;
      setWsConnected(true);
      reconnectAttemptsRef.current = 0;
    };

    ws.onmessage = (raw) => {
      if (!ehAtual()) return;
      try {
        const data = JSON.parse(raw.data);
        if (data && typeof data === "object" && typeof data.type === "string") {
          handleWsEvent(data as FeedWsEvent);
        }
      } catch (err) {
        console.warn("[FeedWS] invalid message payload:", err);
      }
    };

    ws.onerror = () => {
      // ws.onclose will follow and trigger reconnect.
    };

    ws.onclose = (event) => {
      if (!ehAtual()) return;
      setWsConnected(false);
      wsRef.current = null;
      const shouldRetry = !event.wasClean && reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS;
      if (shouldRetry) {
        reconnectAttemptsRef.current += 1;
        reconnectTimeoutRef.current = setTimeout(connect, RECONNECT_INTERVAL_MS);
      }
    };
  }, [handleWsEvent, largarConexao]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    largarConexao();
    setWsConnected(false);
  }, [largarConexao]);

  const reconnectWs = useCallback(() => {
    disconnect();
    reconnectAttemptsRef.current = 0;
    connect();
  }, [connect, disconnect]);

  useEffect(() => {
    if (!currentUserId) return;
    connect();
    return () => disconnect();
  }, [currentUserId, connect, disconnect]);

  // --- Public surface ----------------------------------------------------

  return {
    posts,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: !!query.hasNextPage,
    loadMore: () => {
      if (query.hasNextPage && !query.isFetchingNextPage) {
        void query.fetchNextPage();
      }
    },
    loadMoreError: query.isFetchNextPageError,
    error: query.isFetchNextPageError ? null : ((query.error as Error | null) ?? null),
    wsConnected,
    reconnectWs,
    pinContext,
    composer: {
      create: (input: FeedPostCreateInput) => createMutation.mutateAsync(input),
      isCreating: createMutation.isPending,
    },
    actions: {
      react: async (postId, emoji) => {
        // Story X1.3.3 — offline: enfileira PRIMEIRO; só aplica o toggle otimista
        // se a persistência deu certo (senão a UI mostraria a reação sem nada na
        // fila pra sincronizar). Replay no reconnect.
        const queueReaction = async () => {
          try {
            await enqueue("react_feed_post", { post_id: postId, emoji });
            applyReactionOptimistic(postId, emoji);
          } catch (e) {
            console.debug("[useFeedTimeline] enqueue react falhou:", e);
          }
        };
        if (typeof navigator !== "undefined" && !navigator.onLine) {
          await queueReaction();
          return;
        }
        const chave = `${postId}:${emoji}`;
        const anterior = filaDeCliquesRef.current.get(chave) ?? Promise.resolve();
        const este = anterior
          .catch(() => undefined)
          .then(() => reactMutation.mutateAsync({ postId, emoji }));
        filaDeCliquesRef.current.set(chave, este);
        try {
          await este;
        } catch (err) {
          if (isNetworkError(err)) {
            await queueReaction();
            return;
          }
          throw err;
        } finally {
          if (filaDeCliquesRef.current.get(chave) === este) filaDeCliquesRef.current.delete(chave);
        }
      },
      pin: async (postId, scope, targetSectorId) => {
        await pinMutation.mutateAsync({ postId, scope, targetSectorId });
      },
      remove: async (postId) => {
        await deleteMutation.mutateAsync(postId);
      },
      edit: (postId, content) => editMutation.mutateAsync({ postId, content }),
      votePoll: async (postId, optionId) => {
        await votePollMutation.mutateAsync({ postId, optionId });
      },
    },
  };
}

export default useFeedTimeline;
